import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface UserInfo {
  dept_id: number | null
  username: string
  nickname: string
  email: string | null
  phone: string | null
  id: number
  uuid: string
  avatar: string | null
  status: number
  is_superuser: boolean
  is_staff: boolean
  is_multi_login: boolean
  join_time: string
  last_login_time: string
}

export interface AuthResponse {
  access_token: string
  access_token_type: string
  access_token_expire_time: string
  refresh_token: string
  refresh_token_type: string
  refresh_token_expire_time: string
  user: UserInfo
}

export const useUserStore = defineStore('user', () => {
  const accessToken = ref<string | null>(null)
  const accessTokenType = ref<string | null>(null)
  const accessTokenExpireTime = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const refreshTokenType = ref<string | null>(null)
  const refreshTokenExpireTime = ref<string | null>(null)
  const user = ref<UserInfo | null>(null)

  const isTokenExpired = (iso: string | null) => {
    if (!iso) return true
    const t = Date.parse(iso)
    if (Number.isNaN(t)) return true
    return Date.now() >= t
  }

  const isLoggedIn = computed(() => {
    return !!accessToken.value && !isTokenExpired(accessTokenExpireTime.value)
  })

  const setAuthData = (data: AuthResponse) => {
    accessToken.value = data.access_token
    accessTokenType.value = data.access_token_type
    accessTokenExpireTime.value = data.access_token_expire_time
    refreshToken.value = data.refresh_token
    refreshTokenType.value = data.refresh_token_type
    refreshTokenExpireTime.value = data.refresh_token_expire_time
    user.value = data.user

    localStorage.setItem('ACCESS_TOKEN', data.access_token)
    localStorage.setItem('ACCESS_TOKEN_TYPE', data.access_token_type)
    localStorage.setItem('ACCESS_TOKEN_EXPIRE_TIME', data.access_token_expire_time)
    localStorage.setItem('REFRESH_TOKEN', data.refresh_token)
    localStorage.setItem('REFRESH_TOKEN_TYPE', data.refresh_token_type)
    localStorage.setItem('REFRESH_TOKEN_EXPIRE_TIME', data.refresh_token_expire_time)
    localStorage.setItem('USER_INFO', JSON.stringify(data.user))
  }

  const loadFromStorage = () => {
    accessToken.value = localStorage.getItem('ACCESS_TOKEN')
    accessTokenType.value = localStorage.getItem('ACCESS_TOKEN_TYPE')
    accessTokenExpireTime.value = localStorage.getItem('ACCESS_TOKEN_EXPIRE_TIME')
    refreshToken.value = localStorage.getItem('REFRESH_TOKEN')
    refreshTokenType.value = localStorage.getItem('REFRESH_TOKEN_TYPE')
    refreshTokenExpireTime.value = localStorage.getItem('REFRESH_TOKEN_EXPIRE_TIME')
    const u = localStorage.getItem('USER_INFO')
    user.value = u ? JSON.parse(u) : null
  }

  const clearAuth = () => {
    accessToken.value = null
    accessTokenType.value = null
    accessTokenExpireTime.value = null
    refreshToken.value = null
    refreshTokenType.value = null
    refreshTokenExpireTime.value = null
    user.value = null

    localStorage.removeItem('ACCESS_TOKEN')
    localStorage.removeItem('ACCESS_TOKEN_TYPE')
    localStorage.removeItem('ACCESS_TOKEN_EXPIRE_TIME')
    localStorage.removeItem('REFRESH_TOKEN')
    localStorage.removeItem('REFRESH_TOKEN_TYPE')
    localStorage.removeItem('REFRESH_TOKEN_EXPIRE_TIME')
    localStorage.removeItem('USER_INFO')
  }

  const getAuthHeader = computed(() => {
    const type = accessTokenType.value || 'Bearer'
    const token = accessToken.value || ''
    return token ? `${type} ${token}` : ''
  })

  return {
    accessToken,
    accessTokenType,
    accessTokenExpireTime,
    refreshToken,
    refreshTokenType,
    refreshTokenExpireTime,
    user,
    isLoggedIn,
    getAuthHeader,
    setAuthData,
    loadFromStorage,
    clearAuth
  }
})